export const PLUGIN_URL = 'https://static.anthony.media/plugin/achievements/index.js';

export const setupScripts = [
  {
    id: 'script-tag',
    title: 'Script Tag (Drop-In)',
    description:
      'Use this to add browser achievements directly to any page. The plugin attaches to window.AchievementSystem.',
    language: 'html',
    code: `<script type="module" src="https://static.anthony.media/plugin/achievements/index.js"></script>
<script type="module">
  const achievements = new window.AchievementSystem({
    theme: 'xboxGreen',
    persistence: true,
  });

  achievements.register({
    id: 'first-win',
    title: 'Winner!',
    description: 'Win your first match',
    score: 10,
  });

  achievements.unlock('first-win');
</script>`,
  },
  {
    id: 'bundler',
    title: 'ES Module Import',
    description:
      'Use this when your app uses ESM or a bundler pipeline.',
    language: 'javascript',
    code: `import AchievementSystem from 'https://static.anthony.media/plugin/achievements/index.js';

const achievements = new AchievementSystem({
  theme: 'brightBlue',
  position: 'top-right',
});

achievements.registerMany([
  { id: 'launch', title: 'Booted', score: 5 },
  { id: 'collector', title: 'Collector', maxProgress: 10, score: 25 },
]);`,
  },
];

export const constructorOptions = [
  {
    name: 'staticUrl',
    type: 'string',
    defaultValue: 'https://static.anthony.media/plugin/achievements',
    description: 'Base URL for plugin assets (icons, sprites, and sounds).',
  },
  {
    name: 'theme',
    type: 'string | null',
    defaultValue: 'null',
    description:
      'Preset color theme name. If valid, this sets notification colors from COLOR_PRESETS.',
  },
  {
    name: 'colors',
    type: '[string, string]',
    defaultValue: "['#39960C', '#40a90e']",
    description:
      'Custom [dark, light] color pair used by the notification animation.',
  },
  {
    name: 'soundEnabled',
    type: 'boolean',
    defaultValue: 'true',
    description: 'Enable unlock sounds for common/rare notifications.',
  },
  {
    name: 'position',
    type: 'string',
    defaultValue: 'bottom-center',
    description:
      'Container anchor: top-left, top-center, top-right, bottom-left, bottom-center, bottom-right.',
  },
  {
    name: 'animationDuration',
    type: 'number',
    defaultValue: '10500',
    description: 'Notification animation runtime in milliseconds.',
  },
  {
    name: 'persistence',
    type: 'boolean | adapter',
    defaultValue: 'true',
    description:
      'true uses localStorage. You can pass a custom adapter with save/load/remove methods.',
  },
  {
    name: 'persistenceKey',
    type: 'string',
    defaultValue: 'ui-achievement-system',
    description: 'Storage key used by the default persistence adapter.',
  },
  {
    name: 'autoSave',
    type: 'boolean',
    defaultValue: 'true',
    description: 'Persist automatically when progress or unlock state changes.',
  },
  {
    name: 'autoNotify',
    type: 'boolean',
    defaultValue: 'true',
    description: 'Automatically show popup notifications when unlock() succeeds.',
  },
  {
    name: 'labels',
    type: '{ common: string; rare: string }',
    defaultValue:
      "{ common: 'Achievement Unlocked', rare: 'Rare Achievement Unlocked' }",
    description: 'Text labels rendered in notifications for common and rare unlocks.',
  },
  {
    name: 'customIcon',
    type: 'string | null',
    defaultValue: 'null',
    description: 'Global icon URL shown if achievement definition does not provide icon.',
  },
  {
    name: 'className',
    type: 'string',
    defaultValue: "''",
    description:
      'Extra CSS class appended to each notification root element for custom styling.',
  },
  {
    name: 'debug',
    type: 'boolean',
    defaultValue: 'false',
    description: 'Enable debug logs from internal methods.',
  },
  {
    name: 'queueDelay',
    type: 'number',
    defaultValue: '500',
    description:
      'Delay between queued notifications after one finishes animating out.',
  },
];

export const definitionFields = [
  {
    name: 'id',
    required: 'yes',
    type: 'string',
    defaultValue: '-',
    description: 'Unique identifier used by all API methods.',
  },
  {
    name: 'title',
    required: 'yes',
    type: 'string',
    defaultValue: '-',
    description: 'Display name shown in the notification and query methods.',
  },
  {
    name: 'description',
    required: 'no',
    type: 'string',
    defaultValue: "''",
    description: 'Secondary descriptive text for achievement details.',
  },
  {
    name: 'score',
    required: 'no',
    type: 'number',
    defaultValue: '0',
    description: 'Points assigned to the achievement and included in getStats().',
  },
  {
    name: 'icon',
    required: 'no',
    type: 'string | null',
    defaultValue: 'null',
    description: 'Per-achievement icon override.',
  },
  {
    name: 'category',
    required: 'no',
    type: 'string | null',
    defaultValue: 'null',
    description: 'Category label used by getByCategory() and getCategories().',
  },
  {
    name: 'secret',
    required: 'no',
    type: 'boolean',
    defaultValue: 'false',
    description:
      'If true, locked data is masked by get()/getAll() unless revealSecrets is enabled.',
  },
  {
    name: 'rare',
    required: 'no',
    type: 'boolean',
    defaultValue: 'false',
    description: 'Uses rare label and rare audio/animation variant for notifications.',
  },
  {
    name: 'maxProgress',
    required: 'no',
    type: 'number',
    defaultValue: '1',
    description:
      'Progress threshold. Values above 1 create incremental achievements.',
  },
];

export const viewShapeFields = [
  { name: 'id', type: 'string', description: 'Unique identifier.' },
  { name: 'title', type: 'string', description: 'Display title.' },
  { name: 'description', type: 'string', description: 'Description text.' },
  { name: 'score', type: 'number', description: 'Achievement score value.' },
  { name: 'icon', type: 'string | null', description: 'Icon URL if defined.' },
  { name: 'category', type: 'string | null', description: 'Category name.' },
  {
    name: 'secret',
    type: 'boolean',
    description: 'Whether this achievement is marked secret.',
  },
  {
    name: 'rare',
    type: 'boolean',
    description: 'Whether this achievement uses rare notifications.',
  },
  {
    name: 'maxProgress',
    type: 'number',
    description: 'Required total progress to unlock.',
  },
  {
    name: 'unlocked',
    type: 'boolean',
    description: 'Current unlock state.',
  },
  {
    name: 'unlockedAt',
    type: 'string | null',
    description: 'ISO timestamp when unlock() succeeded.',
  },
  { name: 'progress', type: 'number', description: 'Current progress value.' },
  {
    name: 'progressPercent',
    type: 'number',
    description: 'Computed percentage (0-100) with two decimal precision.',
  },
];

export const eventReference = [
  {
    name: 'registered',
    payload: 'achievementView',
    description: 'Fires after register() succeeds for a definition.',
  },
  {
    name: 'unlocked',
    payload: 'achievementView',
    description: 'Fires when an achievement transitions from locked to unlocked.',
  },
  {
    name: 'progress',
    payload: 'achievementView',
    description: 'Fires whenever progress() or setProgress() updates progress.',
  },
  {
    name: 'revoked',
    payload: 'achievementView',
    description: 'Fires when revoke() resets a previously unlocked achievement.',
  },
  {
    name: 'reset',
    payload: 'none',
    description: 'Fires when reset() clears all unlock/progress state.',
  },
];

export const methodGroups = [
  {
    title: 'Event Bus Methods',
    methods: [
      {
        name: 'on',
        signature: "on(event, handler): this",
        returns: 'this',
        description:
          'Subscribes to an event. Valid events are registered, unlocked, progress, revoked, and reset.',
        code: `achievements.on('unlocked', (achievement) => {
  console.log('Unlocked:', achievement.id, achievement.title);
});`,
      },
      {
        name: 'off',
        signature: 'off(event, handler?): this',
        returns: 'this',
        description:
          'Unsubscribes one handler, or all handlers for the event if handler is omitted.',
        code: `const handleProgress = (achievement) => {
  console.log(achievement.progressPercent + '%');
};

achievements.on('progress', handleProgress);
achievements.off('progress', handleProgress);
// achievements.off('progress'); // removes all progress handlers`,
      },
      {
        name: 'once',
        signature: 'once(event, handler): this',
        returns: 'this',
        description:
          'Subscribes for one event emission only, then automatically unsubscribes.',
        code: `achievements.once('unlocked', (achievement) => {
  analytics.track('first_unlock_seen', { id: achievement.id });
});`,
      },
    ],
  },
  {
    title: 'Configuration Methods',
    methods: [
      {
        name: 'configure',
        signature: 'configure(options): this',
        returns: 'this',
        description:
          'Updates runtime config after construction. Useful for toggling debug, labels, icons, or position.',
        code: `achievements.configure({
  position: 'top-right',
  labels: {
    common: 'Objective Complete',
    rare: 'Legendary Objective Complete',
  },
  debug: true,
});`,
      },
      {
        name: 'setTheme',
        signature: 'setTheme(themeNameOrColors): this',
        returns: 'this',
        description:
          'Applies a preset theme by name or a custom [dark, light] color pair.',
        code: `achievements.setTheme('brightBlue');
achievements.setTheme(['#1f2937', '#334155']);`,
      },
      {
        name: 'getColorPresets',
        signature: 'getColorPresets(): { name, colors }[]',
        returns: 'Array<{ name: string; colors: [string, string] }>',
        description:
          'Returns all built-in theme names and their color values for UI selectors or docs.',
        code: `const presets = achievements.getColorPresets();
console.table(presets);`,
      },
      {
        name: 'mute',
        signature: 'mute(): this',
        returns: 'this',
        description: 'Disables notification audio without changing other behavior.',
        code: `achievements.mute();`,
      },
      {
        name: 'unmute',
        signature: 'unmute(): this',
        returns: 'this',
        description: 'Re-enables notification audio after mute().',
        code: `achievements.unmute();`,
      },
    ],
  },
  {
    title: 'Registration Methods',
    methods: [
      {
        name: 'register',
        signature: 'register(definition): this',
        returns: 'this',
        description:
          'Registers one achievement definition. Throws if id or title is missing.',
        code: `achievements.register({
  id: 'story-chapter-1',
  title: 'Chapter One',
  description: 'Finish chapter one',
  score: 20,
  category: 'story',
  maxProgress: 1,
});`,
      },
      {
        name: 'registerMany',
        signature: 'registerMany(definitions): this',
        returns: 'this',
        description:
          'Registers multiple definitions in order. Throws if input is not an array.',
        code: `achievements.registerMany([
  { id: 'jump-10', title: 'Hop Starter', maxProgress: 10, score: 10 },
  { id: 'jump-100', title: 'Sky Dancer', maxProgress: 100, score: 30, rare: true },
]);`,
      },
      {
        name: 'unregister',
        signature: 'unregister(id): this',
        returns: 'this',
        description: 'Removes both definition and state for a specific achievement ID.',
        code: `achievements.unregister('jump-10');`,
      },
    ],
  },
  {
    title: 'Unlock & Progress Methods',
    methods: [
      {
        name: 'unlock',
        signature: 'unlock(id, { silent = false }?): this',
        returns: 'this',
        description:
          'Unlocks an achievement if currently locked. Can suppress popup with silent: true.',
        code: `achievements.unlock('story-chapter-1');
achievements.unlock('internal-checkpoint', { silent: true });`,
      },
      {
        name: 'progress',
        signature: 'progress(id, amount = 1): this',
        returns: 'this',
        description:
          'Increments progress by amount and auto-unlocks when maxProgress is reached.',
        code: `achievements.progress('jump-100');      // +1
achievements.progress('jump-100', 5);   // +5`,
      },
      {
        name: 'setProgress',
        signature: 'setProgress(id, value): this',
        returns: 'this',
        description:
          'Sets absolute progress (clamped between 0 and maxProgress). Auto-unlocks at max.',
        code: `achievements.setProgress('jump-100', 73);
achievements.setProgress('jump-100', 100); // unlocks`,
      },
      {
        name: 'revoke',
        signature: 'revoke(id): this',
        returns: 'this',
        description:
          'Re-locks an unlocked achievement and resets progress/unlockedAt.',
        code: `achievements.revoke('story-chapter-1');`,
      },
    ],
  },
  {
    title: 'Query Methods',
    methods: [
      {
        name: 'get',
        signature: 'get(id, { revealSecrets = false }?): AchievementView | null',
        returns: 'AchievementView | null',
        description:
          'Returns one achievement view. Locked secret achievements are masked unless revealSecrets is true.',
        code: `const visible = achievements.get('hidden-room');
const full = achievements.get('hidden-room', { revealSecrets: true });`,
      },
      {
        name: 'getAll',
        signature: 'getAll({ revealSecrets = false }?): AchievementView[]',
        returns: 'AchievementView[]',
        description:
          'Returns all achievement views in registration order.',
        code: `const all = achievements.getAll();
const allIncludingSecretText = achievements.getAll({ revealSecrets: true });`,
      },
      {
        name: 'getUnlocked',
        signature: 'getUnlocked(): AchievementView[]',
        returns: 'AchievementView[]',
        description: 'Returns all unlocked achievements with full values.',
        code: `const unlocked = achievements.getUnlocked();
console.log('Unlocked count:', unlocked.length);`,
      },
      {
        name: 'getLocked',
        signature: 'getLocked({ revealSecrets = false }?): AchievementView[]',
        returns: 'AchievementView[]',
        description:
          'Returns currently locked achievements. Secret entries remain masked unless revealSecrets is true.',
        code: `const lockedForUi = achievements.getLocked();
const lockedForAdmin = achievements.getLocked({ revealSecrets: true });`,
      },
      {
        name: 'getByCategory',
        signature:
          'getByCategory(category, { revealSecrets = false }?): AchievementView[]',
        returns: 'AchievementView[]',
        description: 'Filters achievements by a matching category value.',
        code: `const combat = achievements.getByCategory('combat');
const story = achievements.getByCategory('story', { revealSecrets: true });`,
      },
      {
        name: 'isUnlocked',
        signature: 'isUnlocked(id): boolean',
        returns: 'boolean',
        description: 'Checks whether a specific achievement is unlocked.',
        code: `if (achievements.isUnlocked('story-chapter-1')) {
  unlockPremiumSkin();
}`,
      },
      {
        name: 'getStats',
        signature:
          'getStats(): { total, unlocked, locked, percentage, score: { earned, total } }',
        returns:
          '{ total: number; unlocked: number; locked: number; percentage: number; score: { earned: number; total: number } }',
        description:
          'Returns aggregate completion and score metrics across all definitions.',
        code: `const stats = achievements.getStats();
console.log(stats.percentage + '% complete');
console.log('Score', stats.score.earned, '/', stats.score.total);`,
      },
      {
        name: 'getCategories',
        signature: 'getCategories(): string[]',
        returns: 'string[]',
        description: 'Returns unique category names from registered definitions.',
        code: `const categories = achievements.getCategories();`,
      },
    ],
  },
  {
    title: 'Notification Methods',
    methods: [
      {
        name: 'notify',
        signature: 'notify(id): this',
        returns: 'this',
        description:
          'Manually enqueues a popup for an achievement view, even if already unlocked.',
        code: `achievements.notify('story-chapter-1');`,
      },
      {
        name: 'clearNotificationQueue',
        signature: 'clearNotificationQueue(): this',
        returns: 'this',
        description:
          'Clears pending queued notifications while leaving the current active popup untouched.',
        code: `achievements.clearNotificationQueue();`,
      },
    ],
  },
  {
    title: 'Persistence Methods',
    methods: [
      {
        name: 'save',
        signature: 'save(): this',
        returns: 'this',
        description: 'Writes current state to the active persistence adapter immediately.',
        code: `achievements.save();`,
      },
      {
        name: 'load',
        signature: 'load(): this',
        returns: 'this',
        description: 'Loads persisted state and merges it into current in-memory state.',
        code: `achievements.load();`,
      },
      {
        name: 'reset',
        signature: 'reset(): this',
        returns: 'this',
        description:
          'Clears all progress/unlocks for registered IDs and removes persisted storage data.',
        code: `achievements.reset();`,
      },
    ],
  },
  {
    title: 'Lifecycle Method',
    methods: [
      {
        name: 'destroy',
        signature: 'destroy(): void',
        returns: 'void',
        description:
          'Fully tears down queues, listeners, styles, and notification container. Do not use the instance after destroy().',
        code: `achievements.destroy();`,
      },
    ],
  },
];

export const scriptRecipes = [
  {
    id: 'bootstrap',
    title: 'Full Bootstrap Script',
    description:
      'A complete starter setup with registry, progress-driven achievement, and event handlers.',
    language: 'javascript',
    code: `const achievements = new window.AchievementSystem({
  theme: 'xboxGreen',
  position: 'bottom-right',
  labels: {
    common: 'Achievement Unlocked',
    rare: 'Rare Achievement Unlocked',
  },
  debug: false,
});

achievements.registerMany([
  {
    id: 'first-win',
    title: 'Winner!',
    description: 'Win your first game',
    score: 10,
    category: 'gameplay',
  },
  {
    id: 'combo-master',
    title: 'Combo Master',
    description: 'Land 50 combos',
    score: 25,
    category: 'gameplay',
    maxProgress: 50,
  },
  {
    id: 'nightmare-clear',
    title: 'Nightmare Clear',
    description: 'Beat the game on nightmare mode',
    score: 100,
    rare: true,
    category: 'difficulty',
  },
]);

achievements.on('unlocked', (a) => {
  console.log('Unlocked:', a.title);
});

function onGameWon() {
  achievements.unlock('first-win');
}

function onComboLanded() {
  achievements.progress('combo-master', 1);
}

function onNightmareCompleted() {
  achievements.unlock('nightmare-clear');
}`,
  },
  {
    id: 'custom-persistence',
    title: 'Custom Persistence Adapter',
    description:
      'Replace localStorage with your own save/load implementation.',
    language: 'javascript',
    code: `const cloudAdapter = {
  save: (key, data) => {
    fetch('/api/achievements/save', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ key, data }),
    });
  },
  load: (key) => {
    const raw = sessionStorage.getItem('demo-cache:' + key);
    return raw ? JSON.parse(raw) : null;
  },
  remove: (key) => {
    sessionStorage.removeItem('demo-cache:' + key);
  },
};

const achievements = new window.AchievementSystem({
  persistence: cloudAdapter,
  autoSave: true,
  persistenceKey: 'my-game-profile-42',
});`,
  },
  {
    id: 'admin-debug',
    title: 'Admin and QA Debug Script',
    description:
      'Utilities for testing unlock state, queues, and cleanup during development.',
    language: 'javascript',
    code: `window.achievements = new window.AchievementSystem({
  debug: true,
  autoNotify: true,
  persistence: false,
});

window.achievements.register({
  id: 'qa-unlock',
  title: 'QA Unlock',
  score: 1,
});

window.forcePop = () => {
  window.achievements.revoke('qa-unlock').unlock('qa-unlock');
};

window.printStats = () => {
  console.log(window.achievements.getStats());
};

window.resetAchievements = () => {
  window.achievements.reset();
};

window.disposeAchievements = () => {
  window.achievements.destroy();
};`,
  },
];

export const playableExamples = [
  {
    id: 'play-init',
    title: 'Initialize Runtime',
    description:
      'Loads the plugin and creates the demo AchievementSystem instance.',
    actionKey: 'init',
    language: 'javascript',
    code: `const achievements = new window.AchievementSystem({
  theme: 'xboxGreen',
  position: 'bottom-right',
  persistence: false,
});`,
  },
  {
    id: 'play-common',
    title: 'Pop Common Achievement',
    description:
      'Replays a common achievement unlock so the notification animation appears.',
    actionKey: 'commonUnlock',
    language: 'javascript',
    code: `achievements.revoke('demo-common');
achievements.unlock('demo-common');`,
  },
  {
    id: 'play-rare',
    title: 'Pop Rare Achievement',
    description:
      'Triggers a rare unlock to preview rare label, animation, and sound.',
    actionKey: 'rareUnlock',
    language: 'javascript',
    code: `achievements.revoke('demo-rare');
achievements.unlock('demo-rare');`,
  },
  {
    id: 'play-progress',
    title: 'Add Incremental Progress',
    description:
      'Applies +10 progress to an incremental achievement and returns current view data.',
    actionKey: 'addProgress10',
    language: 'javascript',
    code: `achievements.progress('demo-marathon', 10);
console.log(achievements.get('demo-marathon', { revealSecrets: true }));`,
  },
  {
    id: 'play-complete',
    title: 'Complete Incremental Achievement',
    description:
      'Sets progress to max to force unlock for an incremental achievement.',
    actionKey: 'completeProgress',
    language: 'javascript',
    code: `achievements.revoke('demo-marathon');
achievements.setProgress('demo-marathon', 100);`,
  },
  {
    id: 'play-notify',
    title: 'Manual notify() Preview',
    description:
      'Queues a popup with notify(id) even if state is already unlocked.',
    actionKey: 'notifyCommon',
    language: 'javascript',
    code: `achievements.notify('demo-common');`,
  },
  {
    id: 'play-theme',
    title: 'Set Theme + Notify',
    description:
      'Switches to brightBlue and immediately notifies to preview the color change.',
    actionKey: 'setThemeBlue',
    language: 'javascript',
    code: `achievements.setTheme('brightBlue');
achievements.notify('demo-common');`,
  },
  {
    id: 'play-position',
    title: 'Move Notification Position',
    description:
      'Moves notifications to top-right, then previews one popup.',
    actionKey: 'positionTopRight',
    language: 'javascript',
    code: `achievements.configure({ position: 'top-right' });
achievements.notify('demo-common');`,
  },
  {
    id: 'play-mute',
    title: 'Mute Notification Sound',
    description:
      'Turns off sound without changing unlock or animation behavior.',
    actionKey: 'mute',
    language: 'javascript',
    code: `achievements.mute();`,
  },
  {
    id: 'play-unmute',
    title: 'Unmute Notification Sound',
    description:
      'Turns sound back on for new notifications.',
    actionKey: 'unmute',
    language: 'javascript',
    code: `achievements.unmute();`,
  },
  {
    id: 'play-stats',
    title: 'Read Stats Snapshot',
    description:
      'Reads aggregate totals, completion percentage, and score.',
    actionKey: 'stats',
    language: 'javascript',
    code: `console.log(achievements.getStats());`,
  },
  {
    id: 'play-reset',
    title: 'Reset Demo Progress',
    description:
      'Resets all unlock/progress state in the current demo instance.',
    actionKey: 'reset',
    language: 'javascript',
    code: `achievements.reset();`,
  },
];

export const constructorSnippet = `const achievements = new window.AchievementSystem({
  theme: 'xboxGreen',
  position: 'bottom-center',
  soundEnabled: true,
  animationDuration: 10500,
  persistence: true,
  autoSave: true,
  autoNotify: true,
});`;

export const colorPresetSnippet = `// Static preset map is available on the class
console.log(window.AchievementSystem.COLOR_PRESETS);

// Standalone export when imported as module
// import { COLOR_PRESETS } from './achievements/index.js';`;

export const eventSnippet = `const unsubscribeProgress = (achievement) => {
  console.log(achievement.id, achievement.progressPercent + '%');
};

achievements.on('registered', (a) => console.log('Registered', a.id));
achievements.on('unlocked', (a) => console.log('Unlocked', a.id));
achievements.on('progress', unsubscribeProgress);
achievements.on('revoked', (a) => console.log('Revoked', a.id));
achievements.on('reset', () => console.log('All reset'));

// Remove a specific handler when needed
achievements.off('progress', unsubscribeProgress);`;

export const methodCount = methodGroups.reduce(
  (total, group) => total + group.methods.length,
  0,
);
